export type SellerEventName =
  | 'post_property_click'
  | 'property_form_start'
  | 'image_uploaded'
  | 'property_submitted'
  | 'property_updated'

export const SELLER_EVENTS: readonly SellerEventName[] = [
  'post_property_click',
  'property_form_start',
  'image_uploaded',
  'property_submitted',
  'property_updated',
]

export interface SellerListingContext {
  propertyId?: number | null
  cityId?: string | number | null
  areaId?: string | number | null
  categoryId?: string | number | null
  /** Posting form intent: 'sell' | 'rent'. */
  intent?: string | null
}

export type SellerEventParams = Record<string, string | number>

function positiveInt(value: unknown): number | undefined {
  const s = String(value ?? '').trim()
  if (!/^\d+$/.test(s)) return undefined
  const n = Number(s)
  return n > 0 ? n : undefined
}

/** Same intent vocabulary as property events: a 'sell' listing serves buy demand. */
export function sellerIntent(intent?: string | null): 'buy' | 'rent' | undefined {
  const v = (intent ?? '').toLowerCase().trim()
  if (v === 'sell' || v === 'sale' || v === 'buy') return 'buy'
  if (v === 'rent' || v === 'lease') return 'rent'
  return undefined
}

/** IDs only. Never name, mobile, WhatsApp, address, landmark, GPS, description or price. */
export function sellerListingParams(context: SellerListingContext, source: string): SellerEventParams {
  const params: SellerEventParams = { source }
  const ids: Array<[string, unknown]> = [
    ['property_id', context.propertyId],
    ['city_id', context.cityId],
    ['area_id', context.areaId],
    ['category_id', context.categoryId],
  ]
  for (const [key, raw] of ids) {
    const n = positiveInt(raw)
    if (n !== undefined) params[key] = n
  }
  const intent = sellerIntent(context.intent)
  if (intent) params.intent = intent
  return params
}

/** `image_uploaded` is one event per successful batch request (the product uploads all photos at once). */
export function imageUploadedParams(propertyId: number, imageCount: number, source: string): SellerEventParams | null {
  const id = positiveInt(propertyId)
  if (id === undefined || !Number.isInteger(imageCount) || imageCount <= 0) return null
  return { property_id: id, image_count: imageCount, source }
}

/** Fires `onSuccess` only after `send` resolves; failures rethrow without an event. */
export async function afterSuccess<T>(send: () => Promise<T>, onSuccess: (result: T) => void): Promise<T> {
  const result = await send()
  onSuccess(result)
  return result
}

/** Once per wizard session, on the first real (DOM) user interaction. */
export function formStartOnce() {
  let started = false
  return {
    claim(): boolean {
      if (started) return false
      started = true
      return true
    },
  }
}

const CLICK_WINDOW_MS = 1500

export function postPropertyClickGate() {
  let lastAt = -Infinity
  return {
    claim(nowMs: number): boolean {
      if (nowMs - lastAt < CLICK_WINDOW_MS) return false
      lastAt = nowMs
      return true
    },
  }
}
